import {
  LayoutDashboard,
  Users,
  Database,
  Building2,
  Box,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  UserCog,
  Shield,
  Network,
  ArrowRightLeft,
  AlertCircle,
  FileBarChart,
  LineChart,
  Upload,
  CheckSquare,
  ShieldCheck,
  BarChart3,
  ListChecks,
  Activity,
  Contact,
  TrendingUp,
  Briefcase,
  Building,
  UserCheck,
  PieChart,
  Percent
} from 'lucide-react';
import { useMemo, useState, useEffect } from "react";
import { get, post } from "../../utils/api";
import { Menu, MenuItem, Sidebar, SubMenu, useProSidebar } from "react-pro-sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import neoHeader from "../../images/neoHeader.png";
import { useSidebarContext } from "./context/SidebarState";
import styles from "./Sidebar.module.css";
import Footer from "../Footer/Footer";

// Icon mapping helper using lucide-react
const getIcon = (title) => {
  const iconSize = 18;
  const map = {
    "Dashboard": <LayoutDashboard size={iconSize} />,
    "User Management": <Users size={iconSize} />,
    "User Master": <UserCog size={iconSize} />,
    "Role Master": <Shield size={iconSize} />,
    "UI Configuration": <Settings size={iconSize} />,
    "Fund Master": <Database size={iconSize} />,
    "Distributor Master": <Building2 size={iconSize} />,
    "Product Master": <Box size={iconSize} />,
    "Distributor Product Mapped": <Network size={iconSize} />,
    "Product Mapped": <Network size={iconSize} />,
    "Transaction": <ArrowRightLeft size={iconSize} />,
    "Transaction Fee Mismatch": <AlertCircle size={iconSize} />,
    "Transaction Details Report": <FileBarChart size={iconSize} />,
    "Admin Dashboard": <LineChart size={iconSize} />,
    "Invoice Upload": <Upload size={iconSize} />,
    "Invoice Approve": <CheckSquare size={iconSize} />,
    "Invoice": <FileText size={iconSize} />,
    "Distributor Dashboard": <PieChart size={iconSize} />,
    "Sales Dashboard": <TrendingUp size={iconSize} />,
    "KYC": <ShieldCheck size={iconSize} />,
    "KYC Listing": <ListChecks size={iconSize} />,
    "Transaction Management": <Activity size={iconSize} />,
    "Sales Listing": <Contact size={iconSize} />,
    "Sales Head Dashboard": <TrendingUp size={iconSize} />,
    "Distributor Listing": <Briefcase size={iconSize} />,
    "AMC Listing": <Building size={iconSize} />,
    "Transaction Approval": <UserCheck size={iconSize} />,
    "Distributor Management": <Percent size={iconSize} />,
    "Distributor Commercials Mapping": <Percent size={iconSize} />,
    "Default": <FileText size={iconSize} />,
  };
  return map[title] || map["Default"];
};

function SidebarPro() {
  const { isMoreSidebar, setIsMoreSidebar } = useSidebarContext();
  const { collapseSidebar, collapsed } = useProSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [kycStatus, setKycStatus] = useState("");
  const [distributorStatus, setDistributorStatus] = useState("");
  const [isDistributor, setIsDistributor] = useState(false);
  const [hasPendingListingItem, setHasPendingListingItem] = useState(false);

  useEffect(() => {
    const fetchDistributorStatus = async () => {
      const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");
      const userEmail = userDetails?.email;
      const dist = Number(userDetails?.role_id) === 2;
      setIsDistributor(dist);

      if (!dist || !userEmail) {
        return;
      }

      try {
        const response = await get(`/onboarding/getDistributorByEmail?email=${encodeURIComponent(userEmail)}`);
        if (response.data?.Success && response.data.Data) {
          setKycStatus(response.data.Data.kyc_status || "");
          setDistributorStatus(response.data.Data.distributor_status || "");
        } else {
          setKycStatus("");
          setDistributorStatus("");
        }

        const listingResp = await post("/onboarding/fetchDistributorListing", {
          distributor_email: userEmail,
        });

        if (listingResp.data?.Success && Array.isArray(listingResp.data.Data) && listingResp.data.Data.length > 0) {
          const hasPending = listingResp.data.Data.some((item) => {
            const distStatus = (item.distributor_status || "").trim().toLowerCase();
            const agreementStatus = (item.agreement_status || "").trim().toLowerCase();
            const salesHeadStatus = (item.sales_head_status || "").trim().toLowerCase();

            const isFullyApproved =
              distStatus === "approved" &&
              (agreementStatus.includes("executed") ||
                agreementStatus.includes("e-sign completed") ||
                agreementStatus.includes("esign completed") ||
                agreementStatus === "approved");

            return !isFullyApproved;
          });
          setHasPendingListingItem(hasPending);
        } else {
          setHasPendingListingItem(false);
        }
      } catch (error) {
        console.error("Failed to fetch distributor status for sidebar:", error);
      }
    };

    fetchDistributorStatus();
  }, [location.pathname]);

  const renderKycLabel = (title) => {
    const isKycPending = isDistributor && (!kycStatus || String(kycStatus).trim() === "" || String(kycStatus).toLowerCase() === "pending");
    const isDistributorListingPending =
      isDistributor &&
      (hasPendingListingItem ||
        !distributorStatus ||
        String(distributorStatus).trim() === "" ||
        String(distributorStatus).toLowerCase() === "pending");

    if (String(title).toUpperCase() === "KYC" && isKycPending) {
      return (
        <div className={styles.menuLabelWithBadge}>
          <span className={styles.menuLabelTitle} title={title}>{title}</span>
          {collapsed ? (
            <span className={styles.blinkDot} title="KYC Pending" />
          ) : (
            <span className={styles.blinkBadge}>Pending</span>
          )}
        </div>
      );
    }

    if (String(title).toLowerCase() === "distributor listing" && isDistributorListingPending) {
      return (
        <div className={styles.menuLabelWithBadge}>
          <span className={styles.menuLabelTitle} title={title}>{title}</span>
          {collapsed ? (
            <span className={styles.blinkDot} title="Distributor Listing Pending" />
          ) : (
            <span className={styles.blinkBadge}>Pending</span>
          )}
        </div>
      );
    }

    return String(title).toLowerCase() === "distributor commercials mapping"
      ? "Distributor Management"
      : title;
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleSidebar = () => {
    collapseSidebar();
    setIsMoreSidebar(!collapsed);
  };

  const menuTree = useMemo(() => {
    const rawData = JSON.parse(localStorage.getItem("ui_details")) || [];
    const visibleItems = rawData.filter((item) => item.is_view === true);

    const map = {};
    const roots = [];

    visibleItems.forEach((item) => {
      map[item.id] = { ...item, children: [] };
    });

    visibleItems.forEach((item) => {
      if (item.parent_id === 0) {
        roots.push(map[item.id]);
      } else if (map[item.parent_id]) {
        map[item.parent_id].children.push(map[item.id]);
      }
    });

    roots.sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );

    roots.forEach((root) => {
      if (root.children && root.children.length > 0) {
        root.children.sort((x, y) =>
          (x.title || "").localeCompare(y.title || "", undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );
      }
    });

    return roots;
  }, []);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          position: "relative",
          zIndex: 1000,
          transition: "width 0.3s",
        }}
      >
        <Sidebar
          breakPoint="sm"
          transitionDuration={300}
          width="250px"
          collapsedWidth="80px"
          style={{ height: "100vh" }}
          rootStyles={{
            "&.ps-sidebar-root": {
              borderRight: "none !important",
            },
            ".ps-sidebar-container": {
              borderRight: "none !important",
              backgroundColor: "#FFF",
              height: "100% !important",
              display: "flex !important",
              flexDirection: "column !important",
              fontFamily: "Inter, system-ui, sans-serif !important",
              paddingBottom: "0 !important",
            },
            ".ps-menu-root": {
              borderRight: "none !important",
            },
            ".ps-menu-item": {
              marginBottom: '6px !important',
            },
            ".ps-menu-button": {
              padding: '6px 20px !important',
              height: 'auto !important',
              color: '#475569 !important',
              fontWeight: '500 !important',
              fontSize: '13.5px !important',
              transition: 'all 0.2s ease !important',
            },
            ".ps-menu-button:hover": {
              backgroundColor: 'transparent !important',
              color: '#4F46E5 !important',
            },
            ".ps-active .ps-menu-button": {
              backgroundColor: '#EEF2FF !important',
              color: '#4F46E5 !important',
              borderRadius: '8px !important',
              margin: '0 8px !important',
              width: 'calc(100% - 16px) !important',
            },
            ".ps-submenu-content": {
              backgroundColor: "#FFF !important",
              paddingLeft: '12px !important',
            }
          }}
        >
          {/* Sidebar Header Section */}
          <div className="relative flex-none flex items-center justify-center p-3 transition-all duration-300">
            <div className="flex items-center justify-center">
              <img className={styles.logoImg} src={neoHeader} alt="Logo" />
            </div>
            <div
              onClick={toggleSidebar}
              className="p-1 bg-gray-100 rounded-l-lg rounded-r-none text-gray-500 hover:bg-gray-200 cursor-pointer transition-all shrink-0 absolute right-0 border-y border-l border-gray-200 shadow-sm"
              title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </div>
          </div>

          {/* Dotted Divider */}
          <div className="mx-0 mb-4 border-b border-gray-100 opacity-60"></div>

          {/* Scrollable menu section */}
          <div className="flex-1 overflow-y-auto pt-2 scrollbar-thin">
            <Menu>
              {menuTree.map((item) =>
                item.children.length > 0 ? (
                  <SubMenu
                    key={item.id}
                    label={renderKycLabel(item.title)}
                    icon={getIcon(item.title)}
                    active={location.pathname.includes(item.path)}
                  >
                    {item.children.map((child) => (
                      <MenuItem
                        key={child.id}
                        icon={getIcon(child.title)}
                        onClick={() => handleNavigation(child.path)}
                        active={location.pathname === child.path}
                      >
                        {renderKycLabel(child.title)}
                      </MenuItem>
                    ))}
                  </SubMenu>
                ) : (
                  <MenuItem
                    key={item.id}
                    icon={getIcon(item.title)}
                    onClick={() => handleNavigation(item.path)}
                    active={location.pathname === item.path}
                  >
                    {renderKycLabel(item.title)}
                  </MenuItem>
                )
              )}
            </Menu>
          </div>

          {/* Sidebar Footer */}
          <div className="mt-auto flex-none">
            <Footer isSidebar={true} collapsed={collapsed} />
          </div>
        </Sidebar>
      </div>
    </>
  );
}

export default SidebarPro;
